import { observer } from 'mobx-react';
import * as React from 'react';

import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { useClickTracker } from '@shared-ui/clickstream-analytics-context';

import { SearchCriteriaModel } from 'src/common/stores/models';

import { CollapseHeight } from 'constants/collapse-height';
import { FilterModel, FilterOptionModel } from 'components/common/search-form/fields/filter-types';
import { SortAndFilterModel } from './filter-types';

import { UitkExpandoPeek } from 'uitk-react-expando';
import { UitkHeading } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridColumnRow, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

import { getCollapseHeight } from 'utils/get-collapse-height';

import { ActionDetailsType } from '__generated__/api';
import { CheckboxGroupExpandoList } from './checkbox-group-expando-list';
import { CheckboxInput } from 'components/common/search-form/fields/checkbox-input';
import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';

const CheckboxGroupComponent = ({
  getFilterType,
  isChecked,
  model,
  modelKey,
  sortAndFilterOptions,
  updateModel,
  handleChange,
}: {
  getFilterType?: (option: FilterOptionModel) => string;
  isChecked?: (option: FilterOptionModel) => boolean;
  model: SearchCriteriaModel;
  modelKey: string;
  sortAndFilterOptions: SortAndFilterModel;
  updateModel: any;
  handleChange?: (evt) => void;
}) => {
  const [viewExpanded, setViewExpanded] = React.useState(false);
  const track = useClickTracker();

  const checkboxChange = evt => {
    const target = evt.target;
    const { value, checked } = target;

    const checkbox: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const checkboxOptions: FilterOptionModel[] = checkbox && checkbox.options;
    const checkboxName: string = checkbox && checkbox.title;
    const modelValue = model && model[checkboxName];
    const isRange = checkbox && checkbox.range;

    if (typeof updateModel !== 'function' || modelValue === undefined) {
      return;
    }

    /* add/remove checked/unchecked values from the array */
    let newArrayValue = (typeof modelValue === 'string' && modelValue.split(',')) || modelValue.slice();
    const index = newArrayValue.indexOf(value);
    if (checked && index === -1) {
      newArrayValue.push(value);
    } else if (!checked && index !== -1) {
      newArrayValue = newArrayValue.filter(item => item !== value);
    } else {
      // The unlikely scenario where submitted value is already where it should be.
      return;
    }

    /* Update first/last value when isRange true e.g. price */
    if (isRange && newArrayValue.length > 0) {
      const { firstIndex, lastIndex } = getFirstAndLastIndices(checkboxOptions, newArrayValue);
      newArrayValue = [checkboxOptions[firstIndex].value, checkboxOptions[lastIndex].value];
    }

    updateModel({
      target: {
        name: checkboxName,
        value: newArrayValue,
      },
    });
  };

  const getFirstAndLastIndices = (checkboxOptions: FilterOptionModel[], modelValues: string[]) => {
    let firstIndex: number;
    let lastIndex: number;

    checkboxOptions.forEach((option, i) => {
      const optionChecked = modelValues.includes(option.value);

      if (firstIndex === undefined && optionChecked) {
        firstIndex = i;
      }
      if (optionChecked) {
        lastIndex = i;
      }
    });

    return { firstIndex, lastIndex };
  };

  const checkbox = sortAndFilterOptions && sortAndFilterOptions[modelKey];
  const checkboxOptions = checkbox && checkbox.options;
  const showCollapseState = checkbox?.dropdownOptions?.isDropDownVisible;
  const showListState = checkbox?.showListOptions;
  const collapseHeight =
    showListState && showListState.hiddenThreshold ? getCollapseHeight(showListState.hiddenThreshold) : CollapseHeight.TEN_HEIGHT;

  if (!checkboxOptions || checkboxOptions.length === 0) {
    return null;
  }

  return (
    <>
      {showCollapseState ? (
        <CheckboxGroupExpandoList
          getFilterType={getFilterType}
          isChecked={isChecked}
          model={model}
          modelKey={modelKey}
          sortAndFilterOptions={sortAndFilterOptions}
          handleChange={handleChange || checkboxChange}
          checkbox={checkbox}
          getFirstAndLastIndices={getFirstAndLastIndices}
          isVisible={showCollapseState === ActionDetailsType.OPEN}
        />
      ) : (
        <UitkSpacing margin={{ blockstart: 'six' }}>
          <fieldset>
            <UitkSpacing margin={{ blockend: 'two' }}>
              <legend className="filter-title">
                <UitkHeading size={7} tag="h4">
                  {checkbox.label}
                </UitkHeading>
              </legend>
            </UitkSpacing>
            {showListState.hiddenThreshold && checkboxOptions.length > showListState.hiddenThreshold ? (
              <div data-stid="checkbox-group-expando-peek">
                <UitkExpandoPeek
                  collapseHeight={collapseHeight}
                  handleClick={() => setViewExpanded(!viewExpanded)}
                  isVisible={viewExpanded}
                  analytics={{
                    id: checkbox.title,
                    callback: id => {
                      const idArr = id.split('.');
                      track(`${idArr[0]}.ViewAllFilters.${idArr[1]}`, `${checkbox.title}.ViewAllFilters`);
                    },
                  }}
                  hideScrim
                  expandLabel={showListState.seeMoreLabel}
                  collapseLabel={showListState.seeLessLabel}
                >
                  <Checkboxes
                    getFilterType={getFilterType}
                    getFirstAndLastIndices={getFirstAndLastIndices}
                    handleChange={handleChange || checkboxChange}
                    isChecked={isChecked}
                    model={model}
                    modelKey={modelKey}
                    sortAndFilterOptions={sortAndFilterOptions}
                  />
                </UitkExpandoPeek>
              </div>
            ) : (
              <Checkboxes
                getFilterType={getFilterType}
                getFirstAndLastIndices={getFirstAndLastIndices}
                handleChange={handleChange || checkboxChange}
                isChecked={isChecked}
                model={model}
                modelKey={modelKey}
                sortAndFilterOptions={sortAndFilterOptions}
              />
            )}
          </fieldset>
        </UitkSpacing>
      )}
    </>
  );
};

export const Checkboxes = observer(
  ({ getFilterType, getFirstAndLastIndices, handleChange, isChecked, model, modelKey, sortAndFilterOptions }) => {
    const singleColumnNames = ['price', 'travelerType', 'accessibility'];
    const checkbox: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const checkboxOptions: FilterOptionModel[] = checkbox && checkbox.options;
    const checkboxName: string = checkbox && checkbox.title;
    const isRange = checkbox && checkbox.range;

    const values = (model && model[checkboxName]) || [];

    const { firstIndex = {}, lastIndex = {} } = (isRange && getFirstAndLastIndices(checkboxOptions, values)) || {};

    const getCheckboxes = (columns: UitkLayoutGridColumnRow) => (
      <UitkSpacing padding={{ inline: 'one' }}>
        <div>
          <UitkLayoutGrid columns={columns}>
            {checkboxOptions.map((option, i) => (
              <UitkLayoutGridItem key={`${checkboxName}-item-${i}`}>
                <CheckboxInput
                  key={`${checkboxName}-${i}`}
                  index={i}
                  checked={typeof isChecked === 'function' ? isChecked(option) : values.includes(option.value)}
                  handleChange={handleChange}
                  firstIndex={firstIndex}
                  lastIndex={lastIndex}
                  name={checkboxName}
                  ariaLabel={option.ariaLabel}
                  label={option.label}
                  value={option.value}
                  isRange={isRange}
                  description={option.description}
                  filterType={getFilterType && getFilterType(option)}
                />
              </UitkLayoutGridItem>
            ))}
          </UitkLayoutGrid>
        </div>
      </UitkSpacing>
    );

    const getCheckboxLayout = () => (
      <Viewport>
        <ViewSmall>{getCheckboxes(singleColumnNames.includes(checkboxName) ? 1 : 2)}</ViewSmall>
        <ViewLarge>{getCheckboxes(1)}</ViewLarge>
      </Viewport>
    );

    return (
      <React.Fragment>
        <Experiment name="PWA_JPSmallCrafts_HoldOut">
          <ExperimentControl>{getCheckboxes(1)}</ExperimentControl>
          <ExperimentVariant bucket={1}>{getCheckboxLayout()}</ExperimentVariant>
          <ExperimentVariant bucket={2}>{getCheckboxLayout()}</ExperimentVariant>
        </Experiment>
      </React.Fragment>
    );
  }
);

export const CheckboxGroup = FormFieldBinding(CheckboxGroupComponent);
